import { getHours } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import { getServerNow } from "@src/appV2/lib/clockOffset";
import { type TimeSlot } from "@src/appV2/redesign/Shift/types";
import { addMinutes, differenceInMinutes, getHours as getLocalHours, isBefore } from "date-fns";

import {
  MAXIMUM_SHIFT_DURATION_MINUTES,
  MINIMUM_SHIFT_DURATION_MINUTES,
  TIME_SLOT_BOUNDARIES,
  type TimeValidationError,
} from "../types";

const fallbackTu: TuFunction = (defaultText) => defaultText;
import { type TuFunction } from "@src/appV2/i18n/types";

const PAST_START_TIME_BUFFER_MINUTES = 2;

function formatHour(hour: number): string {
  const amPm = hour >= 12 ? "PM" : "AM";

  let displayHour = hour;
  if (hour === 0) {
    displayHour = 12;
  } else if (hour > 12) {
    displayHour = hour - 12;
  }

  return `${displayHour}:00 ${amPm}`;
}

interface ValidateShiftTimesOptions {
  startTime: Date;
  endTime: Date;
  timeSlot: TimeSlot;
  timezone?: string;
  /**
   * When true, validates that start time is at least 2 minutes in the future.
   * Used for invites which don't support extended bookability.
   */
  validatePastStartTime?: boolean;
  /**
   * When true, validates that the start time falls within the current timeslot's boundary.
   * Use when timeslot changes are not allowed (e.g. when posting a new shift).
   * When false (default), timeslot changes are permitted and shown as an info message instead.
   */
  validateTimeslotBoundary?: boolean;
  /**
   * The workplace's *static* pre-configured start and end times for this slot
   * (sourced from the workplace's slot defaults, NOT the shift's current saved
   * time). When provided alongside `validateTimeslotBoundary`, the effective
   * slot range is widened to also include this default range, so admins can
   * edit within (and including) the workplace's intended hours even if those
   * defaults span beyond the strict slot bounds (e.g. PM defaults configured as
   * 11pm-2am).
   *
   * These must NOT be bound to the shift's mutable current time: doing so lets
   * each incremental save advance the accepted range, walking an AM shift into
   * PM/NOC hours one save at a time (SYN-2147). Anchoring to the static slot
   * defaults keeps the accepted range fixed across saves.
   */
  slotDefaultStartTime?: Date;
  slotDefaultEndTime?: Date;
}

function getHourInZone(date: Date, timezone?: string): number {
  return timezone ? getHours(date, { timeZone: timezone }) : getLocalHours(date);
}

function isHourInRange(hour: number, startHour: number, endHour: number): boolean {
  if (startHour <= endHour) {
    return hour >= startHour && hour <= endHour;
  }
  // Wraparound range (e.g. 23-2 covers hours 23, 0, 1, 2).
  return hour >= startHour || hour <= endHour;
}

function isHourInStrictBounds(hour: number, timeSlot: TimeSlot): boolean {
  const boundaries = TIME_SLOT_BOUNDARIES[timeSlot];
  return boundaries.crossesMidnight
    ? hour >= boundaries.startMin || hour <= boundaries.startMax
    : hour >= boundaries.startMin && hour <= boundaries.startMax;
}

function isStartTimeValidForSlot(
  startTime: Date,
  timeSlot: TimeSlot,
  timezone?: string,
  slotDefaultStartTime?: Date,
  slotDefaultEndTime?: Date
): boolean {
  const hour = getHourInZone(startTime, timezone);

  if (isHourInStrictBounds(hour, timeSlot)) {
    return true;
  }

  // If the workplace has pre-configured defaults that span beyond the strict
  // slot bounds (e.g. PM 11pm-2am), accept any hour that falls within that
  // configured range too. This widens the effective slot bounds rather than
  // dropping validation entirely. The range is anchored to the static slot
  // defaults, so it does not move as the shift's time is edited and saved.
  if (isDefined(slotDefaultStartTime) && isDefined(slotDefaultEndTime)) {
    const slotDefaultStartHour = getHourInZone(slotDefaultStartTime, timezone);
    const slotDefaultEndHour = getHourInZone(slotDefaultEndTime, timezone);
    if (isHourInRange(hour, slotDefaultStartHour, slotDefaultEndHour)) {
      return true;
    }
  }

  return false;
}

function getShiftDurationMinutes(startTime: Date, endTime: Date): number {
  let duration = differenceInMinutes(endTime, startTime);

  if (duration < 0) {
    duration += 24 * 60;
  }

  return duration;
}

export function validateShiftTimes(
  {
    startTime,
    endTime,
    timeSlot,
    timezone,
    validatePastStartTime = false,
    validateTimeslotBoundary = false,
    slotDefaultStartTime,
    slotDefaultEndTime,
  }: ValidateShiftTimesOptions,
  tu: TuFunction = fallbackTu
): TimeValidationError[] {
  const errors: TimeValidationError[] = [];

  // For invites: validate that start time is at least 2 minutes in the future
  if (validatePastStartTime) {
    const now = getServerNow();
    const minStartTime = addMinutes(now, PAST_START_TIME_BUFFER_MINUTES);

    if (isBefore(startTime, minStartTime)) {
      errors.push({
        field: "start",
        message: tu("The start time must be at least 2 minutes after the current time"),
      });
    }
  }

  if (
    validateTimeslotBoundary &&
    !isStartTimeValidForSlot(
      startTime,
      timeSlot,
      timezone,
      slotDefaultStartTime,
      slotDefaultEndTime
    )
  ) {
    const boundaries = TIME_SLOT_BOUNDARIES[timeSlot];
    let message = `Start time must be between ${formatHour(boundaries.startMin)} and ${formatHour(
      boundaries.startMax
    )}`;

    // When the workplace's pre-configured defaults extend the valid range
    // beyond the strict slot bounds (e.g. PM defaults of 11pm-2am), surface
    // that wider range in the error so admins know both ranges are accepted.
    if (isDefined(slotDefaultStartTime) && isDefined(slotDefaultEndTime)) {
      const slotDefaultStartHour = getHourInZone(slotDefaultStartTime, timezone);
      const slotDefaultEndHour = getHourInZone(slotDefaultEndTime, timezone);
      const extendsStrictBounds =
        !isHourInStrictBounds(slotDefaultStartHour, timeSlot) ||
        !isHourInStrictBounds(slotDefaultEndHour, timeSlot);
      if (extendsStrictBounds) {
        message += ` or between ${formatHour(slotDefaultStartHour)} and ${formatHour(
          slotDefaultEndHour
        )}`;
      }
    }

    errors.push({ field: "start", message });
  }

  const duration = getShiftDurationMinutes(startTime, endTime);

  if (duration < MINIMUM_SHIFT_DURATION_MINUTES) {
    errors.push({
      field: "duration",
      message: `Shift duration must be at least ${MINIMUM_SHIFT_DURATION_MINUTES} minutes`,
    });
  }

  if (duration > MAXIMUM_SHIFT_DURATION_MINUTES) {
    errors.push({
      field: "duration",
      message: `Shift duration can't be longer than ${MAXIMUM_SHIFT_DURATION_MINUTES / 60} hours`,
    });
  }

  if (validateTimeslotBoundary && isBefore(endTime, startTime) && timeSlot !== "noc") {
    const boundaries = TIME_SLOT_BOUNDARIES[timeSlot];
    if (!boundaries.crossesMidnight) {
      errors.push({
        field: "end",
        message: tu("End time cannot be before start time for this shift type"),
      });
    }
  }

  return errors;
}

export function hasValidationErrors(errors: TimeValidationError[]): boolean {
  return errors.length > 0;
}

export function getErrorForField(
  errors: TimeValidationError[],
  field: TimeValidationError["field"]
): string | undefined {
  return errors.find((error) => error.field === field)?.message;
}
